import { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys, type Busy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import {
  getAgeGroups, getWeightClasses, previewBracket, updateRegistration, deleteRegistration,
} from '../../api/client';
import { apiError } from '../../hooks/useApiCall';
import { useI18n, formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { useAuthStore } from '../../store/auth';
import AthleteAvatar from '../../components/shared/AthleteAvatar';
import GenderBadge from '../../components/shared/GenderBadge';
import EmptyState from '../../components/shared/EmptyState';
import Field from '../../components/shared/Field';
import { PencilIcon, CheckIcon, XMarkIcon, ScaleIcon, PrinterIcon, BoltIcon, ChevronRightIcon } from '../../components/shared/Icons';
import ModalBackdrop from '../../components/shared/ModalBackdrop';
import ModalHeader from '../../components/shared/ModalHeader';
import ModalFooter from '../../components/shared/ModalFooter';
import type { BracketPreview } from '../../components/tournament/BracketPreviewModal';
import type {
  Gender, Tournament, Registration, Bracket, Team,
  AgeGroup, WeightClass,
} from '../../types';
import { compareCategories, isAgeGroupAllowed } from '../../utils/tournament';
import { drawsAfterCheckin } from '../../utils/tournamentType';
import { managesUnlocked, coachEditsLocked, registrationLocked } from '../../utils/tournamentState';
import { personName } from '../../utils/personName';
import { weightText } from '../../utils/weight';
import { BADGE_XXS } from '../../components/shared/badgeSize';

interface RegistrationsTabProps {
  id: string;
  tournament: Tournament;
  registrations: Registration[];
  /** A local write of the list, under the order guard of the hook (#664). */
  patchRegistrations: (fn: (prev: Registration[]) => Registration[]) => void;
  brackets: Bracket[];
  teams: Team[];
  managesThisTournament: boolean;
  canManageAthletes: boolean;
  tournamentDays: string[];
  setBracketPreview: (v: BracketPreview | null) => void;
  /** The write lock of the bracket draw: each preview is a new IFMA lot (#465). */
  previewDraw: Busy;
  setTab: (tab: string) => void;
  reload: () => Promise<void>;
}

export default function RegistrationsTab({
  id, tournament, registrations, patchRegistrations, brackets,
  managesThisTournament, canManageAthletes, tournamentDays,
  setBracketPreview, previewDraw,
  setTab, reload, }: RegistrationsTabProps) {
  const { showError, showToast } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  // #605 — one write at a time: the Save and the Remove of the Edit Registration
  // modal share one lock, and the confirm box has a lock for each registration.
  const regBusy = useBusy();
  const confirms = useBusyKeys();
  const { t } = useI18n();
  // #547 — a hybrid weighs in first and draws its brackets after End Check-in
  // (operator, 2026-10-03). Between the two, the manager moves athletes
  // between classes, so the pencil does not read the registration lock that
  // End Check-in sets. The server takes a class change of a manager under
  // that lock; only "Lock bouts" hides the pencil.
  const classEditAfterCheckin = drawsAfterCheckin(tournament.type);
  const { divisionLabel, ageShort } = useLabels();
  const { hasRole, user } = useAuthStore();

  // The "Confirmed" chip filters the list to the confirmed boxers.
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  // The age groups of the Edit Registration picker. A single-add form sat
  // here, with no code path that opened it; #532 removed it.
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

  // Edit registration
  const [editingReg, setEditingReg] = useState<Registration | null>(null);
  const [editRegForm, setEditRegForm] = useState({ age_group: '', weight_class: '' });
  const [editWeightClasses, setEditWeightClasses] = useState<WeightClass[]>([]);

  // Load the age groups of the Edit Registration picker
  useEffect(() => {
    if (canManageAthletes) getAgeGroups().then(r => setAgeGroups(r.data));
  }, [canManageAthletes]);

  // Load edit weight classes. The latest run writes (#661, bug 6): the age
  // group select fires for each arrow key, so two loads overlap, and the
  // answer of the first one listed the classes of the earlier age group.
  useEffect(() => {
    if (!editingReg || !editRegForm.age_group) { setEditWeightClasses([]); return; }
    const gender = (editingReg.gender || '') as Gender | '';
    if (!gender) return;
    let live = true;
    getWeightClasses({ age_group: editRegForm.age_group, gender })
      .then(r => { if (live) setEditWeightClasses(r.data); })
      .catch(err => { if (live) { setEditWeightClasses([]); showError(err); } });
    return () => { live = false; };
  }, [editRegForm.age_group, editingReg?.id]);

  /** True when the registration is removed. A cancel of the confirm keeps
   *  the Edit Registration modal open. */
  async function handleRemoveRegistration(regId: string): Promise<boolean> {
    // #665 — ask before run(): the question must not hold the lock.
    if (!(await confirm({ message: t.tournament.removeRegistrationConfirm, confirmLabel: t.common.remove, destructive: true }))) return false;
    const removed = await regBusy.run(async () => {
      try {
        await deleteRegistration(id, regId);
      } catch (err) { showError(err); return false; }
      // #419 — the removal committed. A failed refetch is a warning, not an error.
      await afterWrite({ source: 'RegistrationsTab:remove', success: t.tournament.registrationRemoved, refresh: reload });
      return true;
    });
    return removed ?? false;
  }

  async function handleSaveEditReg(e: React.FormEvent) {
    e.preventDefault();
    if (!editingReg) return;
    const regId = editingReg.id;
    await regBusy.run(async () => {
      try {
        await updateRegistration(id, regId, editRegForm);
      } catch (err) { showError(err); return; }
      // #419 — the change committed. A failed refetch is a warning, not an error.
      setEditingReg(null);
      await afterWrite({ source: 'RegistrationsTab:edit', success: t.tournament.registrationUpdated, refresh: reload });
    });
  }

  const printPage = () => window.print();

  // Group registrations by gender → age group → weight class. Everyone sees
  // the full list; the "Confirmed" chip filters it to the confirmed boxers
  // (operator, 2026-09-25). From 2026-04-25 a manager saw ONLY the confirmed
  // boxers while registration was locked and the bouts were not, to pick the
  // available fighters. With nobody confirmed yet, that list was empty and
  // said that nobody had registered. The chip keeps that view, on demand.
  const groupingSource = confirmedOnly ? registrations.filter(r => r.is_confirmed) : registrations;
  // The group order is compareCategories() from utils/tournament.ts — the one
  // order for every category list. A private map here knew only the eight
  // v2.04 codes, so every IFMA v3.057 group tied and the list sorted by weight
  // alone, children between adults (#344).
  const regGroups = groupingSource.reduce<Record<string, Registration[]>>((acc, r) => {
    const key = `${(r.gender || 'unknown').toLowerCase()} · ${r.age_group} · ${r.weight_class}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r); return acc;
  }, {});
  const regGroupKeys = Object.keys(regGroups).sort((a, b) => {
    const [gA, ageA, wA] = a.split(' · ');
    const [gB, ageB, wB] = b.split(' · ');
    return compareCategories(
      { gender: gA, ageGroup: ageA, weightClass: wA },
      { gender: gB, ageGroup: ageB, weightClass: wB },
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap no-print">
        <div className="flex-1" />
        {registrations.length > 0 && (
          <button onClick={printPage} className="btn-outline btn-sm inline-flex items-center gap-1"><PrinterIcon className="w-3 h-3" /> {t.tournament.printAthletes}</button>
        )}
      </div>
      {coachEditsLocked(tournament) && canManageAthletes && !managesThisTournament && (
        <div className="card p-3 bg-amber-50 text-amber-700 text-sm inline-flex items-center gap-1.5" data-testid="coach-edits-locked-notice">
          <PencilIcon className="w-3.5 h-3.5" /> {t.tournament.coachEditsLocked}
        </div>
      )}
      {editingReg && (() => {
        const hasBracket = brackets.some(br =>
          br.age_group === editingReg.age_group &&
          br.weight_class === editingReg.weight_class &&
          (br.gender || '').toLowerCase() === (editingReg.gender || '').toLowerCase()
        );
        // #547 — after End Check-in a hybrid keeps the pencil to MOVE an
        // athlete between classes. Under the registration lock the page
        // offers no removal. Only the page hides it: the server does not
        // refuse a removal by a manager (auth.md § What the locks refuse).
        // #552, #675 — the removal is a text button in the footer. When a bracket
        // holds the class, the button is off and the body says why: a touch screen
        // never shows a tooltip.
        const canRemove = !tournament.registration_locked;
        return (
        <ModalBackdrop onClose={() => setEditingReg(null)} maxWidth="sm" ariaLabel={t.tournament.editRegistration}>
          <ModalHeader
            title={t.tournament.editRegistration}
            subtitle={personName(editingReg.first_name, editingReg.last_name)}
          />
            <form onSubmit={handleSaveEditReg} className="space-y-3">
              <Field label={t.tournament.ageGroupLabel} required>
                <select className="input" value={editRegForm.age_group} data-testid="registration-age-group"
                  onChange={e => setEditRegForm(f => ({ ...f, age_group: e.target.value, weight_class: '' }))}>
                  <option value="">—</option>
                  {/* #532 — the age groups the tournament takes; the current
                      value stays in the list so the select shows it. */}
                  {ageGroups.filter(ag => isAgeGroupAllowed(ag.code, tournament.allowed_age_groups) || ag.code === editRegForm.age_group).map(ag => (
                    <option key={ag.code} value={ag.code}>{ag.code} ({ag.min_age}–{ag.max_age})</option>
                  ))}
                </select>
              </Field>
              <Field label={t.tournament.weightClassLabel} required>
                <select className="input" value={editRegForm.weight_class} data-testid="registration-weight-class"
                  onChange={e => setEditRegForm(f => ({ ...f, weight_class: e.target.value }))}>
                  <option value="">{t.tournament.selectAgeGroupFirst}</option>
                  {editWeightClasses.map(wc => (
                    <option key={wc.id} value={wc.class_label}>{wc.class_label}</option>
                  ))}
                </select>
              </Field>
              {canRemove && hasBracket && (
                <p className="text-xs text-gray-500" data-testid="registration-remove-blocked">{t.tournament.removeRegistrationBracketHint}</p>
              )}
              <ModalFooter saveAsSubmit onCancel={() => setEditingReg(null)}
                actions={canRemove ? [{
                  label: t.tournament.removeRegistration, destructive: true, disabled: hasBracket || regBusy.busy, testId: 'registration-remove',
                  onClick: async () => {
                    if (await handleRemoveRegistration(editingReg.id)) setEditingReg(null);
                  },
                }] : undefined}
                busy={regBusy.busy} />
            </form>
        </ModalBackdrop>
        );
      })()}
      {registrations.length > 0 && (() => {
        const genders = [...new Set(registrations.map(r => (r.gender || '').toLowerCase()).filter(Boolean))];
        const confirmedCount = registrations.filter(r => r.is_confirmed).length;
        return (
          <div className="flex flex-wrap gap-2">
            {genders.length > 1 && genders.map(g => {
              const count = registrations.filter(r => (r.gender || '').toLowerCase() === g).length;
              return (
                <button key={g}
                  onClick={() => document.getElementById(`gender-${g}`)?.scrollIntoView({ behavior: 'smooth' })}
                  className={`badge text-xs cursor-pointer hover:opacity-80 transition-opacity ${g === 'male' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-pink-50 text-pink-700 border-pink-200'}`}>
                  {divisionLabel(g)} ({count})
                </button>
              );
            })}
            <button type="button" onClick={() => setConfirmedOnly(v => !v)}
              aria-pressed={confirmedOnly} data-testid="boxers-confirmed-filter"
              title={confirmedOnly ? t.tournament.showAllBoxers : t.tournament.showConfirmedBoxers}
              className={`badge text-xs inline-flex items-center gap-1 cursor-pointer transition-colors ${
                confirmedOnly ? 'bg-green-600 text-white border-green-600' : 'badge-green hover:opacity-80'}`}>
              <CheckIcon className="w-3 h-3" /> {t.common.confirmed}: {confirmedCount}
            </button>
          </div>
        );
      })()}
      {regGroupKeys.length === 0 && (
        <EmptyState compact testId="boxers-empty"
          title={confirmedOnly && registrations.length > 0 ? t.tournament.noConfirmedBoxers : t.tournament.noBoxersRegistered} />
      )}
      {(() => {
        let lastGender = '';
        return regGroupKeys.map(group => {
          const regs = regGroups[group];
          const sampleReg = regs[0];
          const gender = (sampleReg?.gender || 'unknown').toLowerCase();
          const showGenderHeader = gender !== lastGender;
          if (showGenderHeader) lastGender = gender;
          return (
          <div key={group}>
            {showGenderHeader && (
              <h3 id={`gender-${gender}`} className="text-xs font-bold uppercase tracking-wider text-gray-400 mt-4 mb-2 flex items-center gap-2 scroll-mt-20">
                {divisionLabel(gender)}
              </h3>
            )}
            <div className="card overflow-hidden" data-testid="registration-group"
              data-age-group={sampleReg?.age_group} data-weight-class={sampleReg?.weight_class} data-gender={gender}>
          {/* #552 — the row wraps on a phone, and no pill breaks inside itself. */}
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
            {/* flex-auto: the category pills keep their width, and the
                status pills wrap under them (with flex-1 the pills stacked). */}
            <div className="flex flex-wrap gap-1.5 flex-auto min-w-0">
              {gender && <GenderBadge gender={gender} short size="sm" />}
              {sampleReg?.age_group && <span className="badge badge-gray text-xs whitespace-nowrap">{sampleReg.age_group}</span>}
              {sampleReg?.weight_class && <span data-testid="group-weight-class" className="badge badge-amber text-xs whitespace-nowrap">{sampleReg.weight_class}</span>}
            </div>
            <span className="badge badge-gray text-xs">{(regs).length}</span>
            {(regs).some(r => r.is_confirmed) && (
              <span className={`badge badge-green ${BADGE_XXS} inline-flex items-center gap-0.5`}><CheckIcon className="w-2.5 h-2.5" /> {(regs).filter(r => r.is_confirmed).length}</span>
            )}
            {(() => {
              const existingBr = brackets.find(br =>
                br.age_group === sampleReg?.age_group &&
                br.weight_class === sampleReg?.weight_class &&
                (br.gender || '').toLowerCase() === gender
              );
              if (!existingBr) return null;
              return (<>
                <span className={`badge ${BADGE_XXS} whitespace-nowrap ${existingBr.status === 'scheduled' ? 'badge-blue' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                  {existingBr.status === 'scheduled' ? t.tournament.bracketScheduled : t.tournament.draft}
                </span>
                <button onClick={() => { setTab('brackets'); setTimeout(() => { const el = document.getElementById(`bracket-${existingBr.id}`); el?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 200); }}
                  className={`badge ${BADGE_XXS} bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 cursor-pointer inline-flex items-center gap-0.5 whitespace-nowrap`}>
                  <BoltIcon className="w-2.5 h-2.5" /> {t.tournament.bracket} <ChevronRightIcon className="w-2.5 h-2.5" />
                </button>
              </>);
            })()}
            {(() => {
              const confirmedCount = (regs).filter(r => r.is_confirmed).length;
              const hasExistingBracket = brackets.some(br =>
                br.age_group === sampleReg?.age_group &&
                br.weight_class === sampleReg?.weight_class &&
                (br.gender || '').toLowerCase() === gender
              );
              if (!managesUnlocked(tournament, managesThisTournament) || confirmedCount < 2 || hasExistingBracket) return null;
              return (
              <button
                disabled={previewDraw.busy}
                onClick={() => previewDraw.run(async () => {
                  try {
                    const res = await previewBracket(id, sampleReg.age_group, sampleReg.weight_class, sampleReg.gender);
                    // Cast: api/client types this as Record<string, unknown>;
                    // the modal consumes the richer BracketPreview shape.
                    setBracketPreview(res.data as unknown as BracketPreview);
                  } catch (err) { showError(err); }
                })}
                data-testid="group-bracket-preview"
                className="btn-outline-purple btn-sm inline-flex items-center gap-1 whitespace-nowrap">
                {previewDraw.busy ? '...' : <><BoltIcon className="w-3 h-3" /> {t.tournament.bracket}</>}
              </button>
              );
            })()}
          </div>
          <div className="divide-y divide-gray-100">
            {(regs).map(reg => (
              <div key={reg.id} data-athlete-record-id={reg.athlete_record_id} className="px-4 py-2.5 flex items-center gap-2.5 print-athlete-row">
                {/* #518, #522 — the country flag at the bottom center of the avatar. */}
                <AthleteAvatar
                  className="no-print"
                  firstName={reg.first_name || '?'}
                  lastName={reg.last_name || '?'}
                  photoUrl={reg.photo_url}
                  size="sm"
                  country={reg.country}
                />
                {/* Option 2: Name gets its own full-width row (never competes for space
                    with pills). Row 2 carries every pill: team, operational status
                    (confirmed/weighin/bout), identity stats (exp/age/weight). Pills will
                    flex-wrap to multiple lines if the card is narrow. */}
                <div className="flex-1 min-w-0">
                  {/* Row 1 — Name (left) + Confirmed + bout# pills (right via ml-auto) */}
                  <div className="font-medium text-sm flex items-center gap-1.5">
                    <span className="min-w-0">{personName(reg.first_name, reg.last_name)}</span>
                    {(reg.is_confirmed || (reg.bout_numbers && reg.bout_numbers.length > 0)) && (
                      <span className="ml-auto flex items-center gap-1.5 no-print">
                        {reg.is_confirmed && <span className={`badge badge-green ${BADGE_XXS}`}>{t.common.confirmed}</span>}
                        {reg.bout_numbers && reg.bout_numbers.length > 0 && reg.bout_numbers.map(n => <span key={n} className={`badge ${BADGE_XXS} px-1.5 bg-rose-100 text-rose-700`} title={t.tournament.assignedBout}>#{n}</span>)}
                      </span>
                    )}
                  </div>
                  {/* Row 2 — Pills split into two clusters:
                      Left (team + operational status): Team, Weighin
                      Right (identity stats, ml-auto): Exp, Age, Weight */}
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5 no-print">
                    {reg.team_name && <span className={`badge badge-gray ${BADGE_XXS} px-1.5`} title={t.common.team}>{reg.team_name}</span>}
                    {reg.weighin_days?.filter(wd => wd.status === 'ready').map((wd, i) => (
                      <span key={`r${i}`} className={`badge ${BADGE_XXS} px-1 bg-cyan-50 text-cyan-700 border-cyan-200 inline-flex items-center gap-0.5`}>
                        <ScaleIcon className="w-2.5 h-2.5" /> {formatDay(wd.date, 'EEE d')}
                      </span>
                    ))}
                    {reg.weighin_days?.filter(wd => wd.status === 'absent').map((wd, i) => (
                      <span key={`a${i}`} className={`badge ${BADGE_XXS} px-1 bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-0.5`}>
                        <XMarkIcon className="w-2.5 h-2.5" /> {formatDay(wd.date, 'EEE d')}
                      </span>
                    ))}
                    <span className="ml-auto flex items-center gap-1.5">
                      {/* The coaches see the experience too (operator, 2026-10-09): every caller of the list already gets it. */}
                      {reg.experience_bouts != null && (managesThisTournament || hasRole('admin', 'jury', 'head_coach', 'coach')) && <span className={`badge badge-amber ${BADGE_XXS} px-1.5`} title={t.boutForm.experienceTip} data-testid="reg-experience">{t.boutForm.boutsBadge.replace('{n}', String(reg.experience_bouts))}</span>}
                      {reg.age && <span className={`badge badge-blue ${BADGE_XXS} px-1.5`} title={t.common.age}>{t.boutForm.ageBadge.replace('{n}', String(reg.age))}</span>}
                      {weightText(reg.current_weight_kg) && <span className={`badge badge-indigo ${BADGE_XXS} px-1.5`} title={t.common.weight}>{weightText(reg.current_weight_kg)}</span>}
                    </span>
                  </div>
                  {/* Print-only combined line (hidden on screen) */}
                  <span className="print-athlete-info">{reg.team_name && <span>· {reg.team_name}</span>} {reg.age && <span>· {ageShort(reg.age)}</span>} {weightText(reg.current_weight_kg) && <span>· {weightText(reg.current_weight_kg)}</span>}</span>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  {managesThisTournament && !registrationLocked(tournament) && (
                    <button
                      onClick={() => confirms.run(reg.id, async () => {
                        try {
                          await updateRegistration(id, reg.id, { is_confirmed: !reg.is_confirmed });
                          patchRegistrations(prev => prev.map(r => r.id === reg.id ? { ...r, is_confirmed: !reg.is_confirmed } : r));
                        } catch (err) { showError(err); }
                      })}
                      disabled={confirms.isBusy(reg.id)} aria-busy={confirms.isBusy(reg.id)}
                      data-testid="registration-confirm"
                      data-confirmed={reg.is_confirmed ? 'true' : 'false'}
                      title={reg.is_confirmed ? t.common.confirmed : t.common.notConfirmed}
                      // #552 — the box stays 20 px; the after: area makes the touch target 36 px.
                      className={`relative after:absolute after:-inset-2 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${
                        reg.is_confirmed ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300'
                      }`}>
                      {reg.is_confirmed && <CheckIcon className="w-3 h-3" />}
                    </button>
                  )}
                {managesUnlocked(tournament, managesThisTournament) && (!tournament.registration_locked || classEditAfterCheckin) && (() => {
                  const hasBracket = brackets.some(br =>
                    br.age_group === reg.age_group &&
                    br.weight_class === reg.weight_class &&
                    (br.gender || '').toLowerCase() === (reg.gender || '').toLowerCase()
                  );
                  // #547 — a class change does not move a bout: the bout keeps
                  // its class, and Match Boxers skips an athlete with a bout.
                  // The server refuses it (ATHLETE_ASSIGNED_TO_BOUT).
                  const hasBout = (reg.bout_numbers?.length ?? 0) > 0;
                  return (
                    <button onClick={() => {
                      setEditingReg(reg);
                      setEditRegForm({ age_group: reg.age_group, weight_class: reg.weight_class });
                    }} data-testid="registration-edit" title={hasBout ? t.tournament.regHasBout : undefined}
                      className="btn-outline btn-sm" disabled={hasBracket || hasBout}><PencilIcon className="w-3 h-3" /></button>
                  );
                })()}
                </div>
              </div>
            ))}
          </div>
        </div>
          </div>
        );
        });
      })()}
    </div>
  );
}
